import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { AppStore, ContentItemModel, OperationModel, Loading } from 'redoc';
import { createPortal } from 'react-dom';
import { useConfig } from '@root/hooks/useConfig';
import { Layout, Sep, DrawerOverlay, DrawerGlobal, DrawerContainer } from '@root/styles/Design';
import { H2, XButton } from '@root/styles/Design';
import { RequestEditor } from './RequestEditor';
import { ResponseViewer } from './ResponseViewer';
import { useMemo } from 'react';
import { GlobalReqEditorStyles } from './Design';
import { EndpointTesterResponse } from './Models';

export function useEndpointTester(store: AppStore) {
    const model = useMemo(() => {
        const disposers: (() => void)[] = [];
        let modalContainer: HTMLElement | null = null;
        let initializedStore: AppStore | null = null;

        const dispose = () => {
            disposers.forEach((disposer) => {
                try {
                    disposer();
                } catch (e) {
                    console.error('Error disposing redoc Try-It augmentation', e);
                }
            });
            disposers.splice(0, Infinity);
        };

        const onTryItHandler = { handler: (() => {}) as (openCmd: EndpointTesterOpenCommand) => void };

        function getOperations(items: ContentItemModel[]) {
            const result: OperationModel[] = [];

            const remaining = items.slice();
            while (remaining.length) {
                const item = remaining.pop()!;
                if (item.type === 'operation') {
                    result.push(item);
                }
                if (item.items.length) {
                    remaining.push(...item.items);
                }
            }

            return result;
        }

        const findDomTarget = (operation: OperationModel): HTMLElement | null => {
            const sectionId = `operation/${operation.operationId}`;
            const sectionEl = document.querySelector(`[data-section-id="${sectionId}"]`);
            const reqSamplesEl = sectionEl?.children[1]?.children[1];
            const sampleTabPanelEl = reqSamplesEl?.children[1]?.children[1];
            return sampleTabPanelEl as HTMLElement | null;
        };

        const appendChildEl = (parent: HTMLElement, child: HTMLElement) => {
            parent.appendChild(child);
            disposers.push(() => parent.removeChild(child));
        };

        const appendModalContainerEl = () => {
            if (!modalContainer) {
                modalContainer = document.createElement('div');
                modalContainer.className = 'req-editor-container';

                document.body.appendChild(modalContainer);
            }
            return modalContainer;
        };

        const appendTryItEl = (operation: OperationModel) => {
            const domTarget = findDomTarget(operation);
            if (domTarget) {
                const tryItEl = document.createElement('button');
                const getSelectedExample = () => (domTarget.querySelector('select') as HTMLSelectElement | null)?.value;
                tryItEl.className = 'try-it-button';
                tryItEl.innerText = 'Try It';
                tryItEl.addEventListener('click', () => onTryItHandler.handler({ operation, exampleName: getSelectedExample() }));

                appendChildEl(domTarget, tryItEl);
                return true;
            }
            return false;
        };

        return {
            get modalContainer() {
                return appendModalContainerEl();
            },
            setOnTryItCallback: (callback: (openCmd: EndpointTesterOpenCommand) => void) => {
                onTryItHandler.handler = callback;
            },
            initialize: (store: AppStore) => {
                if (initializedStore !== store) {
                    dispose();
                    const operations = getOperations(store.spec.contentItems);
                    if (operations.length) {
                        const missedOps: OperationModel[] = [];
                        operations.forEach((operation) => {
                            if (!appendTryItEl(operation)) {
                                missedOps.push(operation);
                            }
                        });
                        initializedStore = store;
                        if (missedOps.length) {
                            console.warn('Unable to augment operation with Try-It button for the following operations:', missedOps);
                        }
                    }
                }
            },
        };
    }, []);

    useLayoutEffect(() => model.initialize(store), [store]);

    return {
        component: (
            <>
                <GlobalReqEditorStyles />
                {createPortal(<EndpointTesterDrawer store={store} onTryItHandlerReady={model.setOnTryItCallback} />, model.modalContainer)}
            </>
        ),
    };
}

export interface EndpointTesterOpenCommand {
    operation: OperationModel;
    exampleName?: string;
}
export function EndpointTesterDrawer(props: {
    store: AppStore;
    onTryItHandlerReady: (callback: (openTo: EndpointTesterOpenCommand) => void) => void;
}) {
    const { onTryItHandlerReady } = props;
    const [openCmd, setOpenCmd] = useState<EndpointTesterOpenCommand>();

    const closeDrawer = useCallback(() => {
        setOpenCmd(undefined);
    }, [setOpenCmd]);

    useEffect(() => {
        onTryItHandlerReady(setOpenCmd);
    }, [onTryItHandlerReady, setOpenCmd]);

    const { config, loading: configLoading } = useConfig();

    return (
        <>
            {openCmd ? <DrawerGlobal /> : null}
            <DrawerOverlay onClick={closeDrawer} visible={!!openCmd} />
            <DrawerContainer visible={!!openCmd}>
                {!openCmd ? null : configLoading ? (
                    <Loading color="var(--color-primary)" />
                ) : (
                    <EndpointTesterContent baseUrl={config?.apis.ExternalApi.baseUrl} operation={openCmd.operation} onClose={closeDrawer} />
                )}
            </DrawerContainer>
        </>
    );
}

export function EndpointTesterContent(props: { operation: OperationModel; onClose?: () => void; baseUrl?: string }) {
    const { baseUrl, operation, onClose } = props;
    const [response, setResponse] = useState<null | EndpointTesterResponse>({ status: 0, body: '', headers: {}, durationMs: 0 });

    return (
        <Layout vert h="100%">
            <Layout horz align="center" justify="space-between" py={8} px={16} style={{ background: 'var(--color-dim-bg)' }}>
                <H2>{operation.name}</H2>
                <XButton sizePx={30} onClick={onClose} />
            </Layout>
            <Sep horz thickness={2} />
            <Layout horz fill>
                <RequestEditor operation={operation} baseUrl={baseUrl} onResponse={setResponse} />
                <Sep vert />
                <ResponseViewer response={response} />
            </Layout>
        </Layout>
    );
}
